import { useOidc } from "../../oidcContext";
import { Tab, TabList, SelectTabData, SelectTabEvent, Button } from "@fluentui/react-components";
import { ArrowLeft24Regular, Dismiss24Regular, Open20Regular } from "@fluentui/react-icons";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import DOMPurify from "dompurify";

import { ChatAppResponse, citationToNextDocUrl, getHeaders } from "../../api";
import { getToken, useLogin } from "../../authConfig";
import { MarkdownViewer } from "../MarkdownViewer";
import { SupportingContent } from "../SupportingContent";
import styles from "./AnalysisPanel.module.css";
import { AnalysisPanelTabs } from "./AnalysisPanelTabs";
import { ThoughtProcess } from "./ThoughtProcess";

// Synthetic citation used by the signal-tool agent. It has no backing blob file,
// so it must never be resolved to a "<name>.md" companion (that 404s). Instead the
// citation tab renders the tool facts already present in data_points.text.
// Each tool call now produces a distinct citation of the form
// "Signaldaten: <label>" (e.g. "Signaldaten: Verbindungen erh") so the citations
// panel shows one chip per invocation; the shared "Signaldaten" prefix identifies
// them all as synthetic signal-tool citations.
const SIGNAL_DATA_CITATION = "Signaldaten";

const isSignalDataCitation = (activeCitation: string | undefined): boolean => {
    if (!activeCitation) {
        return false;
    }
    const citationFileName = activeCitation.split("#")[0].split("/").pop() ?? "";
    return citationFileName === SIGNAL_DATA_CITATION || citationFileName.startsWith(`${SIGNAL_DATA_CITATION}:`);
};

interface SignalDataRow {
    name: string;
    meaning: string;
}

interface ParsedSignalData {
    toolHeader: string;
    groupHeader: string;
    rows: SignalDataRow[];
}

// Parse one signal-tool result. Newlines are stripped to spaces upstream
// (clean_source in approach.py), so we cannot split on "\n". Instead we rely on
// the stable text shape:
//   "Signal data (<tool> <args>): <group header>: - name: meaning - name: meaning ..."
// Producer/consumer/trace results have no "- " list, only free text after the header.
const parseSignalData = (item: string): ParsedSignalData => {
    // The text item is "<citation>: <content>" where <citation> is now
    // "Signaldaten: <label>" (e.g. "Signaldaten: Verbindungen erh"). The actual
    // tool content always starts at "Signal data (", so anchor on that marker
    // instead of a fixed prefix length (which varies per label).
    const contentStart = item.indexOf("Signal data (");
    const body = contentStart >= 0 ? item.slice(contentStart).trim() : item.slice(`${SIGNAL_DATA_CITATION}:`.length).trim();

    // Tool header: everything up to and including the first "): " after "Signal data (".
    let toolHeader = "";
    let rest = body;
    const toolMatch = body.match(/^Signal data \(.*?\):\s*/);
    if (toolMatch) {
        toolHeader = toolMatch[0].replace(/:\s*$/, "").trim();
        rest = body.slice(toolMatch[0].length);
    }

    // List entries are separated by " - ". Text before the first " - " is the group header.
    const firstDash = rest.indexOf(" - ");
    let groupHeader = "";
    let listPart = "";
    if (firstDash >= 0) {
        groupHeader = rest.slice(0, firstDash).trim();
        listPart = rest.slice(firstDash + 3); // skip the leading " - "
    } else {
        groupHeader = rest.trim();
    }
    // Strip a trailing ":" from the group header (e.g. "... 52 total):").
    groupHeader = groupHeader.replace(/:\s*$/, "").trim();

    const rows: SignalDataRow[] = [];
    if (listPart) {
        for (const entry of listPart.split(" - ")) {
            const cleaned = entry.trim();
            if (!cleaned) {
                continue;
            }
            const sep = cleaned.indexOf(":");
            const name = sep >= 0 ? cleaned.slice(0, sep).trim() : cleaned;
            const meaning = sep >= 0 ? cleaned.slice(sep + 1).trim() : "";
            rows.push({ name, meaning });
        }
    }

    return { toolHeader, groupHeader, rows };
};

interface Props {
    className: string;
    activeTab: AnalysisPanelTabs;
    onActiveTabChanged: (tab: AnalysisPanelTabs) => void;
    activeCitation: string | undefined;
    citationHeight: string;
    answer: ChatAppResponse;
    onCitationClicked?: (citationFilePath: string) => void;
    onClose?: () => void;
}

export const AnalysisPanel = ({ answer, activeTab, activeCitation, citationHeight, className, onActiveTabChanged, onCitationClicked, onClose }: Props) => {
    const isDisabledThoughtProcessTab: boolean = !answer.context.thoughts;
    const dataPoints = answer.context.data_points;
    const hasSupportingContent = Boolean(
        dataPoints &&
        ((dataPoints.text && dataPoints.text.length > 0) ||
            (dataPoints.images && dataPoints.images.length > 0) ||
            (dataPoints.external_results_metadata && dataPoints.external_results_metadata.length > 0))
    );
    const isDisabledSupportingContentTab: boolean = !hasSupportingContent;
    const isDisabledCitationTab: boolean = !activeCitation;
    const [citation, setCitation] = useState("");
    // Documents opened via links inside the viewer, so the user can go back.
    const [docHistory, setDocHistory] = useState<string[]>([]);
    const internalNavigation = useRef(false);

    const { userManager: client } = useOidc();
    const { t } = useTranslation();

    const fetchCitation = async () => {
        const token = client ? await getToken(client) : undefined;
        if (activeCitation) {
            // The synthetic "Signaldaten" citation has no blob file; its content is
            // rendered directly from data_points.text, so skip the blob fetch.
            if (isSignalDataCitation(activeCitation)) {
                setCitation("");
                return;
            }
            const citationFileName = activeCitation.split("#")[0].split("/").pop() ?? "";
            // eHandbook citations resolve to a companion .md summary rendered by
            // MarkdownViewer (which self-fetches), so no blob fetch is needed here.
            // These are either the legacy "<container>.ehb" citation or a per-unit
            // citation with no file extension (e.g. "fesc").
            if (activeCitation.endsWith(".ehb") || !citationFileName.includes(".")) {
                setCitation("");
                return;
            }
            // Get hash from the URL as it may contain #page=N
            // which helps browser PDF renderer jump to correct page N
            const hashIndex = activeCitation.indexOf("#");
            const originalHash = hashIndex >= 0 ? activeCitation.substring(hashIndex + 1) : "";
            const requestUrl = hashIndex >= 0 ? activeCitation.substring(0, hashIndex) : activeCitation;
            const response = await fetch(requestUrl, {
                method: "GET",
                headers: await getHeaders(token)
            });
            if (!response.ok) {
                setCitation("");
                return;
            }
            const citationContent = await response.blob();
            let citationObjectUrl = URL.createObjectURL(citationContent);
            // Add hash back to the new blob URL
            if (originalHash) {
                citationObjectUrl += "#" + originalHash;
            }
            setCitation(citationObjectUrl);
        }
    };

    useEffect(() => {
        setCitation("");
        fetchCitation();
        // A citation clicked in the answer starts a fresh history.
        if (!internalNavigation.current) {
            setDocHistory([]);
        }
        internalNavigation.current = false;
    }, [activeCitation]);

    const navigateInViewer = (path: string) => {
        if (!onCitationClicked || !activeCitation || path === activeCitation) return;
        internalNavigation.current = true;
        setDocHistory(history => [...history, activeCitation]);
        onCitationClicked(path);
    };

    const navigateBack = () => {
        const previous = docHistory[docHistory.length - 1];
        if (!onCitationClicked || !previous) return;
        internalNavigation.current = true;
        setDocHistory(history => history.slice(0, -1));
        onCitationClicked(previous);
    };

    const renderFileViewer = () => {
        if (!activeCitation) {
            return null;
        }

        // The signal-tool agent emits a synthetic "Signaldaten" citation that has no
        // backing file. Render the tool facts from data_points.text instead of trying
        // to load a non-existent "Signaldaten.md" (which fails with a 404).
        if (isSignalDataCitation(activeCitation)) {
            // Show only the tool facts backing the clicked citation. Each text item
            // is prefixed with its own citation ("<citation>: Signal data (...)"),
            // so match on the exact active citation; fall back to all signal-tool
            // items for the legacy shared "Signaldaten" citation.
            const activeName = activeCitation.split("#")[0].split("/").pop() ?? "";
            const allSignalItems = (answer.context.data_points?.text ?? []).filter(item => item.startsWith(`${SIGNAL_DATA_CITATION}:`));
            const signalItems = activeName === SIGNAL_DATA_CITATION ? allSignalItems : allSignalItems.filter(item => item.startsWith(`${activeName}:`));
            return (
                <div className={styles.signalData}>
                    {signalItems.map((item, ind) => {
                        const { toolHeader, groupHeader, rows } = parseSignalData(item);
                        return (
                            <div key={`signal-data-${ind}`} className={styles.signalDataGroup}>
                                {groupHeader && <div className={styles.signalDataHeading}>{DOMPurify.sanitize(groupHeader)}</div>}
                                {toolHeader && <div className={styles.signalDataTool}>{DOMPurify.sanitize(toolHeader)}</div>}
                                {rows.length > 0 && (
                                    <ul className={styles.signalDataList}>
                                        {rows.map((row, rowInd) => (
                                            <li key={`signal-data-${ind}-${rowInd}`}>
                                                <span className={styles.signalDataName}>{DOMPurify.sanitize(row.name)}</span>
                                                {row.meaning && <span className={styles.signalDataMeaning}>{DOMPurify.sanitize(row.meaning)}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        }

        const citationFileName = activeCitation.split("#")[0].split("/").pop() ?? "";
        // eHandbook unit citations have no file extension (e.g. "fesc") and resolve
        // to a per-unit companion "<unit>.md" summary.
        if (!citationFileName.includes(".")) {
            return <MarkdownViewer src={activeCitation + ".md"} />;
        }

        const fileExtension = citationFileName.split(".").pop()?.toLowerCase();
        switch (fileExtension) {
            case "png":
                return <img src={citation} className={styles.citationImg} alt="Citation Image" />;
            case "md": {
                const nextDocUrl = citationToNextDocUrl(activeCitation);
                return (
                    <>
                        {nextDocUrl && (
                            <a className={styles.openInNextDoc} href={nextDocUrl} target="_blank" rel="noopener noreferrer">
                                <Open20Regular />
                                {t("labels.openInNextDoc")}
                            </a>
                        )}
                        <MarkdownViewer src={activeCitation} onNavigate={navigateInViewer} />
                    </>
                );
            }
            case "ehb":
                // Render the companion .ehb.md summary via MarkdownViewer
                return <MarkdownViewer src={activeCitation + ".md"} />;
            default:
                return <iframe title="Citation" src={citation} width="100%" height={citationHeight} />;
        }
    };

    return (
        <div className={className}>
            <div className={styles.analysisPanelHeader}>
                <TabList
                    selectedValue={activeTab}
                    onTabSelect={(_ev: SelectTabEvent, data: SelectTabData) => onActiveTabChanged(data.value as AnalysisPanelTabs)}
                >
                    <Tab value={AnalysisPanelTabs.ThoughtProcessTab} disabled={isDisabledThoughtProcessTab}>
                        {t("headerTexts.thoughtProcess")}
                    </Tab>
                    <Tab value={AnalysisPanelTabs.SupportingContentTab} disabled={isDisabledSupportingContentTab}>
                        {t("headerTexts.supportingContent")}
                    </Tab>
                    <Tab value={AnalysisPanelTabs.CitationTab} disabled={isDisabledCitationTab}>
                        {t("headerTexts.citation")}
                    </Tab>
                </TabList>
                {onClose && (
                    <Button
                        appearance="subtle"
                        className={styles.analysisPanelCloseButton}
                        icon={<Dismiss24Regular />}
                        aria-label={t("labels.closeButton")}
                        title={t("labels.closeButton")}
                        onClick={onClose}
                    />
                )}
            </div>
            <div>
                {activeTab === AnalysisPanelTabs.ThoughtProcessTab && (
                    <ThoughtProcess thoughts={answer.context.thoughts || []} onCitationClicked={onCitationClicked} />
                )}
                {activeTab === AnalysisPanelTabs.SupportingContentTab && <SupportingContent supportingContent={answer.context.data_points} />}
                {activeTab === AnalysisPanelTabs.CitationTab && docHistory.length > 0 && (
                    <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={navigateBack} title={t("labels.backButton")}>
                        {t("labels.backButton")}
                    </Button>
                )}
                {activeTab === AnalysisPanelTabs.CitationTab && renderFileViewer()}
            </div>
        </div>
    );
};
